/**
 * Talking to the server.
 *
 * Every path is relative, so the same code works against the Vite dev proxy and the single
 * container in production.
 */

import type { CollctivDoc } from '@lp/shared';

export interface DocSummary {
  id: string;
  title: string;
  docType: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
}

export interface VersionSummary {
  id: number;
  label: string | null;
  createdAt: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(body.error ?? res.statusText, res.status);
  }
  return (await res.json()) as T;
}

export const api = {
  /** Whether a password is set, and whether this browser has one. */
  me: () => request<{ authed: boolean; required: boolean }>('/api/auth/me'),

  login: (password: string) =>
    request<{ ok: true }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  listDocs: () => request<{ docs: DocSummary[] }>('/api/docs').then((r) => r.docs),

  getDoc: (id: string) =>
    request<{ doc: CollctivDoc } & DocSummary>(`/api/docs/${encodeURIComponent(id)}`).then(
      (r) => r.doc,
    ),

  saveDoc: (doc: CollctivDoc, label?: string) =>
    request<DocSummary>(
      `/api/docs/${encodeURIComponent(doc.id)}${label ? `?label=${encodeURIComponent(label)}` : ''}`,
      { method: 'PUT', body: JSON.stringify(doc) },
    ),

  deleteDoc: (id: string) =>
    request<{ ok: true }>(`/api/docs/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  listVersions: (id: string) =>
    request<{ versions: VersionSummary[] }>(
      `/api/docs/${encodeURIComponent(id)}/versions`,
    ).then((r) => r.versions),

  restoreVersion: (id: string, versionId: number) =>
    request<{ doc: CollctivDoc }>(
      `/api/docs/${encodeURIComponent(id)}/restore/${versionId}`,
      { method: 'POST' },
    ).then((r) => r.doc),

  /** Server-rendered download, so the filename comes from the slug. */
  exportUrl: (id: string) => `/api/docs/${encodeURIComponent(id)}/export`,
};

/** Is a server there at all? The editor still works without one, against localStorage. */
export async function serverAvailable(): Promise<boolean> {
  try {
    const res = await fetch('/healthz', { method: 'GET' });
    return res.ok;
  } catch {
    return false;
  }
}
